// "As the CSR saw it" (2026-10-29, docs/feedback.md): the pure parts of the
// screen state a Send Feedback report carries and of the read-only view that
// shows it, so they run under node --test without a DOM.
//
// Capture: the panel's own state, never scraped from the DOM. For every step
// (manual-panel steps included): its index, type, label and on-screen state,
// its input fields as the suggestion prefilled them (the panel's
// appliedActionInputsRef, what apply() seeded) and as they were on screen
// (actionInputs), its check result; the choices (Sales Hold path, resolution
// path, moved / not moved, the steps the reply lifted, the steps marked not
// needed or unchecked); the template picked; the AI draft and the reply box's
// exact HTML. The server validates it and stores it unredacted, admin-only
// (server/src/services/feedback-screen-state.js).
//
// View: what the CSR changed from the AI's prefilled value, per step and per
// field ("Reason code: AI 12 → CSR 7"), the version warning, the reply's
// word changes, and the Activity Log's one-line summary of what a processed
// step submitted (action_step_executions.submitted_params).

import { salesHoldPathOf, resolutionOf } from '@cxhub/shared'

// A JSON-safe copy (drops functions, undefined and cycles); null when it
// cannot be copied.
export function jsonCopy(value) {
  if (value === undefined) return null
  try {
    const seen = new WeakSet()
    return JSON.parse(JSON.stringify(value, (k, v) => {
      if (typeof v === 'function') return undefined
      if (v && typeof v === 'object') {
        if (seen.has(v)) return undefined
        seen.add(v)
      }
      return v
    }))
  } catch {
    return null
  }
}

function pathChoice(view) {
  if (!view?.path) return null
  return { path: view.path, preselected: !view.overridden, why: view.why ?? null }
}

function safely(fn) {
  try { return fn() } catch { return null }
}

// The template on the reply: one the CSR inserted (with its id), else the AI
// draft's own pick. null when none.
export function screenTemplate({ insertedTemplate = null, insertedTemplateId = null, modelMeta = null } = {}) {
  if (insertedTemplate?.title) {
    return { id: insertedTemplateId ?? null, title: insertedTemplate.title, folder: insertedTemplate.folder ?? null, source: 'agent_inserted' }
  }
  const ai = modelMeta?.draftTemplate?.title ? modelMeta.draftTemplate : (modelMeta?.selectedTemplateTitle ? { title: modelMeta.selectedTemplateTitle } : null)
  if (!ai) return null
  return { id: ai.id ?? null, title: ai.title, folder: ai.folder ?? null, source: ai.source ?? 'ai_selected' }
}

// The screen state Send Feedback sends with a report (the browser's half;
// the server adds the version and the execution links).
export function buildFeedbackScreenState({
  suggestion = null, context = {}, actionInputs = {}, appliedInputs = {}, stepStates = {}, checkStates = {},
  taskChoice = null, taskChoiceDate = null, notNeededSteps = [], uncheckedSteps = [], skippedSteps = [],
  isShown = null, draft = null, hasBox = false, insertedTemplate = null, insertedTemplateId = null,
  replyMode = null, to = null, subject = null, cc = null, partLinks = {}, now = new Date(),
} = {}) {
  const actions = Array.isArray(suggestion?.proposedActions) ? suggestion.proposedActions : []
  const notNeeded = new Set(notNeededSteps ?? [])
  const unchecked = new Set(uncheckedSteps ?? [])
  const lifted = new Map((skippedSteps ?? []).filter((x) => x && Number.isInteger(x.index)).map((x) => [x.index, x.reason ?? null]))
  const steps = actions.map((a, index) => {
    const st = stepStates?.[index] ?? null
    const ck = checkStates?.[index] ?? null
    return {
      index,
      type: a?.type ?? null,
      label: a?.label ?? a?.title ?? null,
      source: a?.source ?? null,
      shown: typeof isShown === 'function' ? Boolean(safely(() => isShown(a, index))) : null,
      state: st?.status ?? 'pending',
      checkStatus: ck?.status ?? null,
      error: typeof st?.error === 'string' ? st.error : null,
      checked: !unchecked.has(index),
      notNeeded: notNeeded.has(index),
      lifted: lifted.get(index) ?? null,
      inputs: {
        prefilled: jsonCopy(appliedInputs?.[index] ?? null),
        onScreen: jsonCopy(actionInputs?.[index] ?? null),
      },
      checkResult: ck ? jsonCopy(ck.result ?? ck) : null,
    }
  })
  const view = { proposedActions: actions, modelMeta: suggestion?.modelMeta ?? {} }
  return {
    suggestionId: suggestion?.id ?? null,
    capturedAt: now.toISOString(),
    context: { ...context },
    steps,
    choices: {
      salesHoldPath: pathChoice(safely(() => salesHoldPathOf(view))),
      resolutionPath: pathChoice(safely(() => resolutionOf(view))),
      taskChoice: taskChoice ? { choice: taskChoice, date: taskChoiceDate || null } : null,
      skippedSteps: [...lifted.entries()].map(([index, reason]) => ({ index, reason })),
      notNeededSteps: [...notNeeded],
      uncheckedSteps: [...unchecked],
    },
    template: screenTemplate({ insertedTemplate, insertedTemplateId, modelMeta: suggestion?.modelMeta }),
    reply: {
      draft: typeof suggestion?.draftReply === 'string' ? suggestion.draftReply : null,
      box: hasBox && typeof draft === 'string' ? draft : null,
      hasBox: Boolean(hasBox),
      replyMode,
      to,
      subject,
      cc,
    },
    panel: {
      actionInputs: jsonCopy(actionInputs) ?? {},
      appliedInputs: jsonCopy(appliedInputs) ?? {},
      stepStates: jsonCopy(stepStates) ?? {},
      checkStates: jsonCopy(checkStates) ?? {},
      partLinks: jsonCopy(partLinks) ?? {},
    },
  }
}

// --- What the CSR changed --------------------------------------------------

// { 'a.b': 1, 'items[0].qty': 2 }: leaves by path. A bare value is ''.
export function flattenFields(value, prefix = '', out = {}) {
  if (value == null || typeof value !== 'object') {
    out[prefix] = value ?? null
    return out
  }
  if (Array.isArray(value)) {
    if (value.length === 0) out[prefix] = []
    value.forEach((v, i) => flattenFields(v, `${prefix}[${i}]`, out))
    return out
  }
  const keys = Object.keys(value)
  if (keys.length === 0 && prefix) out[prefix] = {}
  for (const k of keys) flattenFields(value[k], prefix ? `${prefix}.${k}` : k, out)
  return out
}

// "reasonCode" → "Reason code", "items[0].quantity" → "Items 1 quantity",
// "editedAddress.shippingCity" → "Edited address shipping city".
export function fieldLabel(path) {
  if (!path) return 'Value'
  const words = String(path)
    .replace(/\[(\d+)\]/g, (_, n) => ` ${Number(n) + 1} `)
    .replace(/\./g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.toLowerCase())
  const text = words.join(' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

// A value as one short line.
export function displayValue(v, max = 120) {
  if (v == null || v === '') return '(empty)'
  const s = typeof v === 'string' ? v : JSON.stringify(v)
  const flat = s.replace(/\s+/g, ' ').trim()
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat
}

// The fields the CSR changed from the AI's prefilled value:
// [{ path, label, ai, csr }]. A field only on screen is a change from empty.
export function changedFields(prefilled, onScreen) {
  if (sameValue(prefilled, onScreen)) return []
  const a = flattenFields(prefilled ?? null)
  const b = flattenFields(onScreen ?? null)
  // A bare null on one side and an object on the other: compare the object's
  // leaves with empty.
  if (prefilled == null) delete a['']
  if (onScreen == null) delete b['']
  const paths = [...new Set([...Object.keys(a), ...Object.keys(b)])]
  return paths
    .filter((p) => !sameValue(a[p], b[p]))
    .filter((p) => !((a[p] == null || a[p] === '') && (b[p] == null || b[p] === '')))
    .map((p) => ({ path: p, label: fieldLabel(p), ai: a[p] ?? null, csr: b[p] ?? null }))
}

// "Reason code: AI 12 → CSR 7"
export function changeLine(c) {
  return `${c.label}: AI ${displayValue(c.ai)} → CSR ${displayValue(c.csr)}`
}

// Each step's changes, and whether anything changed on it (a field, or the
// step marked not needed or unchecked).
export function stepChanges(step) {
  const fields = changedFields(step?.inputs?.prefilled ?? null, step?.inputs?.onScreen ?? null)
  const flags = []
  if (step?.notNeeded) flags.push('Marked "my reply doesn\'t need this"')
  if (step?.checked === false) flags.push('Unchecked')
  return { fields, flags, changed: fields.length > 0 || flags.length > 0 }
}

// The steps a "Changed only" view keeps: their indexes.
export function changedStepIndexes(screenState) {
  return new Set((screenState?.steps ?? []).filter((s) => stepChanges(s).changed).map((s) => s.index))
}

// The version warning: "This report was on version 2 of 3; a newer draft
// exists." null when the report was on the newest.
export function versionWarning(version) {
  const now = version?.now
  if (!now?.newer) return null
  const number = version?.atReport?.number ?? now.number
  return `This report was on version ${number} of ${now.total}; a newer draft exists.`
}

// --- The reply ----------------------------------------------------------------

// HTML as plain text, without a DOM (the view's diff only).
export function htmlText(html) {
  if (typeof html !== 'string') return ''
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

const MAX_DIFF_WORDS = 1500

// Word changes between the AI draft and the box: two lists of
// { text, changed } for the side-by-side view (removed words marked on the
// draft's side, added on the box's). Over MAX_DIFF_WORDS a side is marked
// whole when it differs.
export function replyWordDiff(draft, box) {
  const a = htmlText(draft).split(/(\s+)/).filter((t) => t !== '')
  const b = htmlText(box).split(/(\s+)/).filter((t) => t !== '')
  const words = (list) => list.filter((t) => !/^\s+$/.test(t))
  if (words(a).length > MAX_DIFF_WORDS || words(b).length > MAX_DIFF_WORDS) {
    const same = htmlText(draft) === htmlText(box)
    return { draft: [{ text: a.join(''), changed: !same }], box: [{ text: b.join(''), changed: !same }], changed: !same }
  }
  const n = a.length
  const m = b.length
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
  }
  const left = []
  const right = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { left.push({ text: a[i], changed: false }); right.push({ text: b[j], changed: false }); i++; j++ }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { left.push({ text: a[i], changed: !/^\s+$/.test(a[i]) }); i++ }
    else { right.push({ text: b[j], changed: !/^\s+$/.test(b[j]) }); j++ }
  }
  while (i < n) { left.push({ text: a[i], changed: !/^\s+$/.test(a[i]) }); i++ }
  while (j < m) { right.push({ text: b[j], changed: !/^\s+$/.test(b[j]) }); j++ }
  // Whitespace between two changed words is part of the change.
  const join = (list) => list.map((t, k) => (!t.changed && /^\s+$/.test(t.text) && list[k - 1]?.changed && list[k + 1]?.changed ? { ...t, changed: true } : t))
  const merge = (list) => join(list).reduce((acc, t) => {
    const last = acc.at(-1)
    if (last && last.changed === t.changed) last.text += t.text
    else acc.push({ ...t })
    return acc
  }, [])
  return { draft: merge(left), box: merge(right), changed: left.some((t) => t.changed) || right.some((t) => t.changed) }
}

// --- What a processed step submitted (the Activity Log) ----------------------

const VERB = {
  create_rma: 'Created RMA',
  create_return: 'Created return',
  cancel_order_trq: 'Cancelled order',
  add_order_note: 'Added order note',
  process_replacement: 'Created replacement',
  resend_return_label: 'Sent return label',
  create_return_label_for_replacement: 'Created return label',
  create_ebay_cancellation: 'Requested eBay cancellation',
  create_ebay_refund: 'Refunded on eBay',
  issue_ebay_return_refund: 'Refunded the eBay return',
  refund_freight_trq: 'Refunded freight',
  refund_tax_trq: 'Refunded tax',
  update_shipping_method: 'Changed shipping method',
  send_ebay_member_message: 'Sent eBay message',
}

export function stepVerb(type) {
  if (!type) return 'Processed step'
  if (VERB[type]) return VERB[type]
  if (/message|reply|email/i.test(type)) return 'Sent message'
  return fieldLabel(type)
}

const money = (v) => {
  if (v == null || v === '' || typeof v === 'boolean') return null
  const n = Number(v)
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : null
}

function firstOf(obj, keys) {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k]
  }
  return null
}

function itemText(it) {
  if (!it || typeof it !== 'object') return null
  const id = firstOf(it, ['sku', 'SKU', 'partNumber', 'itemNumber', 'itemId', 'lineItemId', 'lineSequence'])
  const qty = firstOf(it, ['quantity', 'qty', 'quantityToReturn', 'returnQuantity'])
  if (id == null) return null
  return qty != null ? `${id} x${qty}` : String(id)
}

// The one-line summary of a step's submitted_params, e.g. "Created RMA:
// AB123 x1, reason 7, label deduction $8.50". null for a row with none (a
// row from before 2026-10-29): the Activity Log shows nothing extra then.
export function submittedSummary(sp) {
  if (!sp || typeof sp !== 'object') return null
  const f = sp.fields && typeof sp.fields === 'object' ? sp.fields : {}
  const bodies = (Array.isArray(sp.calls) ? sp.calls : []).map((c) => c?.body).filter((b) => b && typeof b === 'object')
  const anywhere = (keys) => firstOf(f, keys) ?? bodies.reduce((acc, b) => acc ?? firstOf(b, keys), null)
  const parts = []
  const items = Array.isArray(f.items) ? f.items : (Array.isArray(f.selectedItems) ? f.selectedItems : null)
  if (items) {
    const list = items.map(itemText).filter(Boolean)
    if (list.length) parts.push(list.slice(0, 4).join(', ') + (list.length > 4 ? ` +${list.length - 4} more` : ''))
  }
  const reason = anywhere(['reasonCode', 'cancelReasonCode', 'returnReasonCode', 'replacementReasonCode'])
  if (reason != null) parts.push(`reason ${reason}`)
  const disposition = anywhere(['disposition', 'dispositionOverride'])
  if (disposition != null && typeof disposition !== 'object') parts.push(`disposition ${disposition}`)
  const method = anywhere(['newShippingMethod', 'shippingMethod'])
  if (method != null && typeof method !== 'object') parts.push(`shipping ${method}`)
  const payment = anywhere(['shippingPaymentOption'])
  if (payment != null) parts.push(`payment ${payment}`)
  const deduction = money(anywhere(['labelDeduction', 'shippingDeduction']))
  if (deduction) parts.push(`label deduction ${deduction}`)
  const amount = money(anywhere(['refundAmount', 'amount', 'totalRefund']))
  if (amount) parts.push(`amount ${amount}`)
  const addr = f.editedAddress ?? f.address ?? f.newAddress ?? null
  if (addr && typeof addr === 'object') {
    const line = [addr.shippingAddress1 ?? addr.address1 ?? addr.newAddress1, addr.shippingCity ?? addr.city].filter(Boolean).join(', ')
    if (line) parts.push(`address ${line}`)
  }
  const template = f.template?.title ?? null
  if (template) parts.push(`template ${template}`)
  const ids = sp.result && typeof sp.result === 'object' ? firstOf(sp.result, ['rmaId', 'erpRmaId', 'returnId', 'replacementId', 'erpReplacementId', 'trackingNumber', 'refundId', 'id']) : null
  if (ids != null) parts.unshift(String(ids))
  const note = firstOf(f, ['editedNote', 'notes', 'note'])
  const sent = sp.sentText ?? firstOf(f, ['editedMessage', 'messageText'])
  if (!parts.length && typeof sent === 'string') parts.push(`"${displayValue(sent, 60)}"`)
  else if (!parts.length && typeof note === 'string') parts.push(`"${displayValue(note, 60)}"`)
  const verb = stepVerb(sp.type)
  return parts.length ? `${verb}: ${parts.join(', ')}` : verb
}

// Every field, for the row's expand: [{ key, value }], the fields first, then
// the text sent, the ids returned and each call's body.
export function submittedFieldRows(sp) {
  if (!sp || typeof sp !== 'object') return []
  const rows = []
  for (const [k, v] of Object.entries(flattenFields(sp.fields ?? {}))) rows.push({ key: fieldLabel(k), value: displayValue(v, 2000) })
  if (sp.sentText) rows.push({ key: 'Text sent', value: sp.sentText })
  for (const [k, v] of Object.entries(sp.result ?? {})) rows.push({ key: `Result: ${fieldLabel(k)}`, value: displayValue(v, 500) })
  for (const c of Array.isArray(sp.calls) ? sp.calls : []) {
    const name = [c.service, c.label].filter(Boolean).join(' ')
    for (const [k, v] of Object.entries(flattenFields(c.body ?? {}))) rows.push({ key: `${name} · ${fieldLabel(k)}`, value: displayValue(v, 2000) })
  }
  if (sp.truncated) rows.push({ key: 'Note', value: 'The call bodies were too large to keep.' })
  return rows
}

// The Activity Log's "Reported here" marker (2026-10-29): ?view=activity&feedback=<id>.
export function feedbackIdFromSearch(search) {
  try {
    const id = new URLSearchParams(search ?? '').get('feedback')
    return id && /^[0-9a-f-]{36}$/i.test(id) ? id : null
  } catch {
    return null
  }
}
